/**
 * Autenticação de demonstração.
 *
 * É o que roda nos testes e quando não há Supabase configurado: mesma
 * superfície, sem rede. As contas são as do protótipo, com a senha única de
 * propósito — este adaptador não deve sugerir que guarda credencial.
 */

import { PAPEIS } from './papeis';
import type {
    CadastroDeTotp, EstadoDoMfa, Impersonacao, Papel, PortaDeAutenticacao, ResultadoDeCadastro, ResultadoLogin, Sessao,
} from './tipos';

const CHAVE = 'vec.sessao';

const MFA_SEM_SERVIDOR = 'A demonstração não tem servidor de autenticação: o MFA só existe com a aplicação ligada ao servidor.';

export const SENHA_DEMO = 'vecchia';

export interface UsuarioDemo {
    email: string;
    nome: string;
    papel: Papel;
    clinicaId: string | null;
    clinica: string | null;
    clinicas: string[];
}

export const USUARIOS: UsuarioDemo[] = [
    { email: 'ana@clinicavecchia.com.br', nome: 'Ana Prado', papel: 'proprietaria', clinicaId: '11111111-1111-4111-8111-111111111111', clinica: 'Vecchia — Jardins', clinicas: ['Vecchia — Jardins', 'Vecchia — Itaim', 'Rede Bellavita'] },
    { email: 'paulo@clinicavecchia.com.br', nome: 'Paulo Menezes', papel: 'profissional', clinicaId: '11111111-1111-4111-8111-111111111111', clinica: 'Vecchia — Jardins', clinicas: ['Vecchia — Jardins'] },
    { email: 'recepcao@clinicavecchia.com.br', nome: 'Bruna Lima', papel: 'recepcao', clinicaId: '11111111-1111-4111-8111-111111111111', clinica: 'Vecchia — Jardins', clinicas: ['Vecchia — Jardins'] },
    { email: 'admin@vecchia.com.br', nome: 'Rafael Nunes', papel: 'super-admin', clinicaId: null, clinica: null, clinicas: [] },
];

/** As clínicas da demonstração, para a faixa dizer em qual se entrou. */
const CLINICAS_DEMO: Record<string, string> = {
    'clinica-vecchia': 'Vecchia — Jardins',
    'clinica-bellavita': 'Clínica Bellavita',
    'clinica-aurora': 'Estética Aurora',
    'clinica-bemviver': 'Clínica Bem Viver',
    'clinica-lumine': 'Studio Lumine',
};

function montar(u: UsuarioDemo): Sessao {
    const p = PAPEIS[u.papel];
    return {
        email: u.email,
        nome: u.nome,
        papel: u.papel,
        perfil: p.perfil,
        permissoes: p.permissoes,
        clinica: u.clinica,
        clinicaId: u.clinicaId,
        clinicas: u.clinicas,
        /* Na demonstração nenhum módulo está desligado: as flags nascem
           ligadas, e é o painel da plataforma que as desliga. */
        modulosDesligados: [],
        entrouEm: new Date().toISOString(),
    };
}

/* localStorage lança em contexto sem armazenamento (janela privada, iframe de
   terceiro), e a aplicação inteira cairia por causa disso. */
function gravar(s: Sessao | null): void {
    try {
        if (s) localStorage.setItem(CHAVE, JSON.stringify(s));
        else localStorage.removeItem(CHAVE);
    } catch {
        /* Sessão só em memória: dura enquanto a aba viver. */
    }
}

export const autenticacaoDemo: PortaDeAutenticacao = {
    async entrar(email: string, senha: string): Promise<ResultadoLogin> {
        const e = String(email ?? '').trim().toLowerCase();
        if (!e || !senha) return { ok: false, erro: 'Informe e-mail e senha para continuar.' };
        const u = USUARIOS.find((x) => x.email === e);
        if (!u) return { ok: false, erro: 'Não encontramos uma conta com este e-mail.' };
        if (senha !== SENHA_DEMO) return { ok: false, erro: 'Senha incorreta. Neste protótipo, a senha é "vecchia".' };
        const sessao = montar(u);
        gravar(sessao);
        return { ok: true, sessao };
    },

    async sair(): Promise<void> {
        gravar(null);
    },

    async recuperar(): Promise<Sessao | null> {
        try {
            const bruto = localStorage.getItem(CHAVE);
            if (!bruto) return null;
            const s = JSON.parse(bruto) as Partial<Sessao> | null;
            return s && typeof s.email === 'string' ? (s as Sessao) : null;
        } catch {
            return null;
        }
    },

    async googleDisponivel(): Promise<boolean> {
        return false;
    },

    async entrarComGoogle(): Promise<{ ok: boolean; erro?: string }> {
        return { ok: false, erro: 'Entrar com Google precisa do servidor: a demonstração não fala com o Google.' };
    },

    async contaSemClinica() {
        return null;
    },

    async criarConta({ email, senha }): Promise<ResultadoDeCadastro> {
        const e = String(email ?? '').trim().toLowerCase();
        if (!e.includes('@')) return { ok: false, erro: 'Informe um e-mail válido.' };
        if (senha.length < 8) return { ok: false, erro: 'A senha precisa de pelo menos 8 caracteres.' };
        if (USUARIOS.some((u) => u.email === e)) {
            return { ok: false, erro: 'Já existe uma conta com este e-mail.' };
        }
        /* Sem servidor não há e-mail para confirmar: a demonstração diz que a
           conta ficaria pendente, que é o que acontece de verdade. */
        return { ok: true, precisaConfirmar: true };
    },

    async pedirRecuperacao(email: string): Promise<{ ok: boolean; erro?: string }> {
        if (!String(email ?? '').includes('@')) return { ok: false, erro: 'Informe um e-mail válido.' };
        /* A resposta é a mesma exista ou não a conta: dizer "não encontramos"
           transformaria a tela num verificador de quem tem cadastro. */
        return { ok: true };
    },

    async iniciarImpersonacao(clinicaId: string, motivo: string): Promise<Impersonacao> {
        /* A mesma recusa do banco: motivo curto não é motivo. */
        if (motivo.trim().length < 10) {
            throw new Error('O motivo precisa dizer por quê: escreva ao menos 10 caracteres.');
        }
        const agora = Date.now();
        return {
            id: `imp-${agora}`,
            clinicaId,
            clinica: CLINICAS_DEMO[clinicaId] ?? clinicaId,
            motivo: motivo.trim(),
            readOnly: true,
            inicio: agora,
            /* Trinta minutos, como o `default` da tabela. */
            expiraEm: agora + 30 * 60 * 1000,
        };
    },

    async encerrarImpersonacao(): Promise<void> {
        /* Sem servidor não há linha para encerrar: quem some é o recorte da
           sessão, e disso cuida `sessao.ts`. */
    },

    async definirNovaSenha(senha: string): Promise<{ ok: boolean; erro?: string }> {
        if (senha.length < 8) return { ok: false, erro: 'A senha precisa de pelo menos 8 caracteres.' };
        return { ok: true };
    },

    /* MFA é do servidor de autenticação. Simular um QR aqui seria mostrar um
       segundo fator que não protege conta nenhuma. */
    async estadoDoMfa(): Promise<EstadoDoMfa> {
        return { disponivel: false, motivo: MFA_SEM_SERVIDOR };
    },

    async cadastrarTotp(): Promise<CadastroDeTotp> {
        throw new Error(MFA_SEM_SERVIDOR);
    },

    async confirmarTotp(): Promise<void> {
        throw new Error(MFA_SEM_SERVIDOR);
    },

    async descartarFator(): Promise<void> {
        throw new Error(MFA_SEM_SERVIDOR);
    },
};
